
import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  makeStyles,
  tokens,
  Button,
  Avatar,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Text,
  OverlayDrawer,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Tooltip,
} from '@fluentui/react-components'
import {
  PeopleTeam24Regular,
  CalendarLtrRegular,
  CalendarMonthRegular,
  Person24Regular,
  SignOut24Regular,
  Globe24Regular,
  Building24Regular,
  Navigation24Regular,
  Dismiss24Regular,
  DocumentBulletList24Regular,
  Settings24Regular,
  BuildingBank24Regular,
  ClipboardTaskListLtr24Regular,
  History24Regular,
} from '@fluentui/react-icons'
import { useAuth } from '../../auth/useAuth'
import { absenceRequestsService } from '../../services/absenceRequestsService'
import { meService } from '../../services/meService'

// --- Breakpoint hook ---
function useBreakpoint() {
  const [width, setWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  )
  useEffect(() => {
    const handler = () => setWidth(window.innerWidth)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  return {
    isMobile: width < 768,
    isTablet: width >= 768 && width < 1200,
    isDesktop: width >= 1200,
  }
}

// --- Styles ---
const useStyles = makeStyles({
  root: { display: 'flex', height: '100vh', overflow: 'hidden' },   // pevná výška → scrolluje jen oblast obsahu (sidebar + horní lišta stojí)

  // Desktop: plná sidebar
  sidebar: {
    width: '240px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 0',
    flexShrink: 0,
  },

  // Tablet: jen ikony
  sidebarCollapsed: {
    width: '56px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '16px 0',
    flexShrink: 0,
  },

  logo: {
    padding: '0 16px 16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: '8px',
  },
  logoCollapsed: {
    padding: '0 0 16px',
    width: '100%',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: '8px',
    display: 'flex',
    justifyContent: 'center',
  },

  navItem: {
    margin: '2px 8px',
    justifyContent: 'flex-start',
    width: 'calc(100% - 16px)',
  },
  navItemIndent: {
    margin: '1px 8px 1px 24px',
    justifyContent: 'flex-start',
    width: 'calc(100% - 32px)',
    fontSize: tokens.fontSizeBase200,
  },
  navItemCollapsed: {
    margin: '4px 0',
    minWidth: '40px',
    width: '40px',
  },

  spacer: { flex: 1 },

  // Main area
  main: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },

  // Desktop/tablet header (vpravo nahoře)
  header: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },

  // Mobile header (hamburger vlevo, avatar vpravo)
  mobileHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  mobileHeaderRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },

  content: { flex: 1, padding: '24px', overflowY: 'auto' },
  contentMobile: { flex: 1, padding: '16px', overflowY: 'auto' },
})

// --- Typy ---
interface NavItem {
  label: string
  path: string
  icon: JSX.Element
  roles?: string[]
  hidden?: boolean           // skrytá položka (bez ohledu na role)
  indent?: boolean           // vizuálně odsazená podmenu položka
  highlightPaths?: string[]  // další cesty které zvýrazní tento navItem
  exactPath?: boolean        // aktivní pouze při přesné shodě cesty (ne startsWith)
}

// --- Komponenta ---
export default function AppLayout({ children }: { children: React.ReactNode }) {
  const styles = useStyles()
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { account, logout, isHR, isAdmin, isManager } = useAuth()
  const { isMobile, isTablet } = useBreakpoint()

  const { data: myProfile, isError: meError } = useQuery({
    queryKey: ['me'],
    queryFn: meService.getMe,
    staleTime: 5 * 60_000,
    retry: false,
  })
  // Skrýt "Moje žádosti" pokud víme, že uživatel NENÍ zaměstnanec:
  // - 404 (profil nenalezen) → contractor bez záznamu nebo neznámý uživatel
  // - personType === 'contractor' → contractor se záznamem
  // Dokud se načítá (myProfile === undefined, meError === false) → zobrazit optimisticky
  const hideMyRequests = meError || myProfile?.personType === 'contractor'

  // Počet čekajících žádostí o nepřítomnost (pro badge "Ke schválení")
  const pendingMyTeamOnly = !isHR && !isAdmin
  const { data: pendingRequests } = useQuery({
    queryKey: ['absences', 'pending', pendingMyTeamOnly],
    queryFn: () => absenceRequestsService.getAll({ status: 1, myTeamOnly: pendingMyTeamOnly }),
    enabled: isHR || isAdmin || isManager,
    staleTime: 60_000,
  })
  const pendingCount = pendingRequests?.length ?? 0
  const [drawerOpen, setDrawerOpen] = useState(false)

  const navItems: NavItem[] = [
    { label: t('nav.employees'), path: '/employees', icon: <PeopleTeam24Regular />, roles: ['HR', 'Admin', 'Manager'], highlightPaths: ['/pension-contributions'] },
    { label: 'Příspěvky na stáří', path: '/pension-contributions', icon: <BuildingBank24Regular />, roles: ['HR', 'Admin'], indent: true },
    { label: t('nav.contractors', 'Dodavatelé'), path: '/contractors', icon: <Building24Regular />, roles: ['HR', 'Admin', 'Manager'] },
    { label: 'Dokumenty', path: '/documents', icon: <ClipboardTaskListLtr24Regular />, roles: ['HR', 'Admin'] },
    { label: 'Všechny žádosti', path: '/absences/all', icon: <DocumentBulletList24Regular />, roles: ['HR', 'Admin'] },
    { label: 'Kalendář', path: '/absences/calendar', icon: <CalendarMonthRegular /> },
    { label: `Ke schválení (${pendingCount})`, path: '/absences', icon: <CalendarLtrRegular />, roles: ['HR', 'Admin', 'Manager'], exactPath: true },
    { label: t('nav.myRequests'), path: '/my-requests', icon: <CalendarLtrRegular />, hidden: hideMyRequests },
    { label: t('nav.myProfile'), path: '/my-profile', icon: <Person24Regular /> },
  ]

  const visibleItems = navItems.filter(item => {
    if (item.hidden) return false
    if (!item.roles) return true
    if (isAdmin   && item.roles.includes('Admin'))   return true
    if (isHR      && item.roles.includes('HR'))      return true
    if (isManager && item.roles.includes('Manager')) return true
    return false
  })

  const toggleLanguage = () => {
    const next = i18n.language === 'cs' ? 'en' : 'cs'
    i18n.changeLanguage(next)
    localStorage.setItem('lang', next)
  }

  const handleNavigation = (path: string) => {
    navigate(path)
    setDrawerOpen(false)
  }

  const isActive = (item: NavItem) =>
    item.exactPath
      ? location.pathname === item.path
      : location.pathname.startsWith(item.path) ||
        (item.highlightPaths?.some(p => location.pathname.startsWith(p)) ?? false)

  const langButton = (
    <Button appearance="subtle" icon={<Globe24Regular />} onClick={toggleLanguage}>
      {i18n.language === 'cs' ? 'EN' : 'CS'}
    </Button>
  )

  const avatarMenu = (
    <Menu>
      <MenuTrigger>
        <Avatar name={account?.name ?? ''} size={32} style={{ cursor: 'pointer' }} />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItem>{account?.username}</MenuItem>
          {(isHR || isAdmin) && (
            <MenuItem icon={<Settings24Regular />} onClick={() => navigate('/admin')}>
              Administrace
            </MenuItem>
          )}
          {isAdmin && (
            <MenuItem icon={<History24Regular />} onClick={() => navigate('/admin/activity-log')}>
              Log aktivit
            </MenuItem>
          )}
          <MenuItem icon={<SignOut24Regular />} onClick={logout}>
            {t('auth.logout')}
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  )

  // === MOBILE ===
  if (isMobile) {
    return (
      <div className={styles.root} style={{ flexDirection: 'column' }}>
        {/* Mobile header */}
        <div className={styles.mobileHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Button
              appearance="subtle"
              icon={<Navigation24Regular />}
              onClick={() => setDrawerOpen(true)}
            />
            <img src="/logo-icon.svg" width={28} height={28} alt="People" />
            <Text size={400} weight="semibold">{t('app.title')}</Text>
          </div>
          <div className={styles.mobileHeaderRight}>
            {langButton}
            {avatarMenu}
          </div>
        </div>

        {/* Overlay Drawer */}
        <OverlayDrawer
          open={drawerOpen}
          onOpenChange={(_, { open }) => setDrawerOpen(open)}
          position="start"
        >
          <DrawerHeader>
            <DrawerHeaderTitle
              action={
                <Button
                  appearance="subtle"
                  icon={<Dismiss24Regular />}
                  onClick={() => setDrawerOpen(false)}
                />
              }
            >
              {t('app.title')}
            </DrawerHeaderTitle>
          </DrawerHeader>
          <DrawerBody>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', paddingTop: '8px' }}>
              {visibleItems.map(item => (
                <Button
                  key={item.path}
                  className={item.indent ? styles.navItemIndent : styles.navItem}
                  appearance={isActive(item) ? 'primary' : 'subtle'}
                  icon={item.icon}
                  onClick={() => handleNavigation(item.path)}
                >
                  {item.label}
                </Button>
              ))}
            </div>
          </DrawerBody>
        </OverlayDrawer>

        {/* Content */}
        <main className={styles.contentMobile}>{children}</main>
      </div>
    )
  }

  // === TABLET — ikony ===
  if (isTablet) {
    return (
      <div className={styles.root}>
        <nav className={styles.sidebarCollapsed}>
          <div className={styles.logoCollapsed}>
            <img src="/logo-icon.svg" width={32} height={32} alt="People" />
          </div>
          {visibleItems.map(item => (
            <Tooltip key={item.path} content={item.label} relationship="label" positioning="after">
              <Button
                className={styles.navItemCollapsed}
                appearance={isActive(item) ? 'primary' : 'subtle'}
                icon={item.icon}
                onClick={() => navigate(item.path)}
              />
            </Tooltip>
          ))}
          <div className={styles.spacer} />
        </nav>

        <div className={styles.main}>
          <div className={styles.header}>
            {langButton}
            {avatarMenu}
          </div>
          <div className={styles.content}>{children}</div>
        </div>
      </div>
    )
  }

  // === DESKTOP — plná sidebar ===
  return (
    <div className={styles.root}>
      <nav className={styles.sidebar}>
        <div className={styles.logo}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src="/logo-icon.svg" width={32} height={32} alt="People" />
            <Text size={500} weight="semibold">{t('app.title')}</Text>
          </div>
        </div>
        {visibleItems.map(item => (
          <Button
            key={item.path}
            className={item.indent ? styles.navItemIndent : styles.navItem}
            appearance={isActive(item) ? 'primary' : 'subtle'}
            icon={item.icon}
            onClick={() => navigate(item.path)}
          >
            {item.label}
          </Button>
        ))}
        <div className={styles.spacer} />
      </nav>

      <div className={styles.main}>
        <div className={styles.header}>
          {langButton}
          {avatarMenu}
        </div>
        <div className={styles.content}>{children}</div>
      </div>
    </div>
  )
}
